import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, AlertTriangle, AlertCircle, CheckCircle2, Sparkles, BarChart2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';
import { DEFAULT_COMPANY_ID, API_BASE_URL } from '../lib/config';

export const Route = createFileRoute('/login')({
  component: LoginComponent,
});

type ServiceStatus = 'checking' | 'up' | 'down';

async function checkHealth(): Promise<ServiceStatus> {
  try {
    const res = await fetch(`${API_BASE_URL}/health`, {
      signal: AbortSignal.timeout(5000),
    });
    return res.ok ? 'up' : 'down';
  } catch {
    return 'down';
  }
}

const FEATURES = [
  {
    icon: BarChart2,
    label: 'AI-powered performance reviews',
    desc: 'Continuous feedback & 9-box placement',
  },
  {
    icon: Sparkles,
    label: '360° psychometric assessments',
    desc: 'Big Five, DISC, Leadership & custom tests',
  },
  {
    icon: Users,
    label: 'Smart recruitment matching',
    desc: 'Rank candidates by competency fit',
  },
];

function LoginComponent() {
  const navigate = useNavigate();
  const { loginWithPassword, isAuthenticated } = useAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [companyId, setCompanyId] = useState(DEFAULT_COMPANY_ID);
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus>('checking');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isAuthenticated) navigate({ to: '/dashboard/' });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const status = await checkHealth();
      if (!cancelled) setServiceStatus(status);
    };
    run();
    intervalRef.current = setInterval(run, 30_000);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Empty companyId = super_admin login (no company). Pass as-is.
      await loginWithPassword(email, password, companyId.trim());
      navigate({ to: '/dashboard/' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isDown = serviceStatus === 'down';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">

      {/* ── Left: brand panel ────────────────────────────────────────────── */}
      <div
        className="
          relative hidden lg:flex lg:w-1/2 flex-col justify-between
          bg-gradient-to-br from-[#27777a] via-[#235f62] to-[#1e4243]
          px-12 py-12 overflow-hidden
        "
      >
        {/* Decorative circles — subtle, teal-tinted */}
        <div className="pointer-events-none select-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-white/5" />
        <div className="pointer-events-none select-none absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full bg-white/5" />
        <div className="pointer-events-none select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5" />

        {/* Logo */}
        <div>
          <img
            src="/assets/logo.svg"
            alt="Pelajari"
            className="h-9 w-auto brightness-0 invert"
          />
        </div>

        {/* Centre copy */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-3">
            <h1 className="text-4xl font-semibold leading-tight tracking-tight text-white">
              Simple. Fast.<br />and Bloom.
            </h1>
            <p className="text-[#a8d8da] text-base leading-relaxed max-w-xs">
              The all-in-one HR intelligence platform built for high-growth teams.
            </p>
          </div>

          {/* Feature pills */}
          <div className="space-y-3">
            {FEATURES.map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3.5 border border-white/10"
              >
                <div className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-tight">{label}</p>
                  <p className="text-xs text-[#a8d8da] mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <p className="relative z-10 text-xs text-white/40">
          © 2026 Pelajari by Bloom.AI
        </p>
      </div>

      {/* ── Mobile brand strip (< lg) ────────────────────────────────────── */}
      <div className="lg:hidden bg-gradient-to-r from-[#27777a] to-[#235f62] px-6 py-5 flex items-center justify-between">
        <img
          src="/assets/logo.svg"
          alt="Pelajari"
          className="h-7 w-auto brightness-0 invert"
        />
        <p className="text-xs text-white/60 font-medium tracking-wide uppercase">
          HR Intelligence
        </p>
      </div>

      {/* ── Right: login form ─────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-10">
        <div className="w-full max-w-sm space-y-7">

          {/* Heading */}
          <div className="space-y-1.5">
            <h2 className="text-2xl font-semibold tracking-tight text-[#000f0f]">
              Welcome back
            </h2>
            <p className="text-sm text-[#878989]">
              Sign in to your workspace to continue
            </p>
          </div>

          {/* Service unavailable banner */}
          {isDown && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Service Unavailable</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  The platform is currently unavailable. Please wait for further announcement.
                </p>
              </div>
            </div>
          )}

          {/* Login error */}
          {error && !isDown && (
            <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Service checking indicator */}
          {serviceStatus === 'checking' && (
            <div className="flex items-center gap-2 text-xs text-[#878989]">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#359d9e] animate-pulse" />
              Checking service status…
            </div>
          )}

          {/* Service up confirmation (shown once, then fades into nothing) */}
          {serviceStatus === 'up' && !error && (
            <div className="flex items-center gap-2 text-xs text-emerald-600">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Service is online
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-medium text-[#000f0f]">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isDown}
                required
                placeholder="you@company.com"
                className="
                  w-full rounded-lg border border-[#d5f2ef] bg-white px-3.5 py-2.5 text-sm
                  text-[#000f0f] placeholder:text-[#b3b7b7]
                  focus:outline-none focus:ring-2 focus:ring-[#359d9e] focus:border-transparent
                  disabled:bg-[#f2fbfa] disabled:text-[#878989] disabled:cursor-not-allowed
                  transition-shadow
                "
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-[#000f0f]">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isDown}
                  required
                  placeholder="••••••••"
                  className="
                    w-full rounded-lg border border-[#d5f2ef] bg-white px-3.5 py-2.5 pr-10 text-sm
                    text-[#000f0f] placeholder:text-[#b3b7b7]
                    focus:outline-none focus:ring-2 focus:ring-[#359d9e] focus:border-transparent
                    disabled:bg-[#f2fbfa] disabled:text-[#878989] disabled:cursor-not-allowed
                    transition-shadow
                  "
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#878989] hover:text-[#359d9e] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword
                    ? <EyeOff className="w-4 h-4" />
                    : <Eye className="w-4 h-4" />
                  }
                </button>
              </div>
            </div>

            {/* Advanced (Company ID) */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs text-[#359d9e] hover:text-[#27777a] transition-colors flex items-center gap-1"
              >
                <span>{showAdvanced ? '▾' : '▸'}</span>
                Advanced options
              </button>
              {showAdvanced && (
                <div className="mt-2.5 space-y-1.5">
                  <label htmlFor="companyId" className="block text-sm font-medium text-[#000f0f]">
                    Company ID
                  </label>
                  <input
                    id="companyId"
                    type="text"
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    disabled={isDown}
                    placeholder="11111111-1111-1111-1111-111111111111"
                    className="
                      w-full rounded-lg border border-[#d5f2ef] bg-white px-3.5 py-2.5 text-sm font-mono
                      text-[#000f0f] placeholder:text-[#b3b7b7]
                      focus:outline-none focus:ring-2 focus:ring-[#359d9e] focus:border-transparent
                      disabled:bg-[#f2fbfa] disabled:text-[#878989] disabled:cursor-not-allowed
                      transition-shadow
                    "
                  />
                  <p className="text-xs text-[#878989]">
                    Leave blank and clear this field for Pelajari superadmin login.
                  </p>
                </div>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || isDown}
              className="
                relative w-full rounded-lg bg-[#359d9e] px-4 py-2.5 text-sm font-semibold text-white
                hover:bg-[#27777a] active:scale-[0.99]
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-150 flex items-center justify-center gap-2
                shadow-[0px_4px_20px_0px_rgba(53,157,158,0.25)]
              "
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4 text-white/80" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-[#b3b7b7]">
            Need help?{' '}
            <a
              href="mailto:support@pelajari.ai"
              className="text-[#359d9e] hover:text-[#27777a] transition-colors"
            >
              support@pelajari.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
